import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { addFriend, ignoreUser, removeFriend } from '../../../actions/user'
import { closeQuickMenu } from '../../../app-state/modals/state.confirmation'
import {
    openAuthenticationDrawer,
    openReportUserDrawer,
} from '../../../app-state/modals/state.drawer'
import { openQuickReply } from '../../../app-state/modals/state.floater'
import { FloaterSize } from '../../../constants/enums'
import { isTouchDevice } from '../../../utilities/browser'
import { getModelColor, getUserColor } from '../../../utilities/colors'
import IconButton from '../../Buttons/IconButton'
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState'
import { useLayoutSelector } from '../../Hooks/useLayout'
import { useQuickMenu } from '../../Hooks/useModal'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useUserSelector } from '../../Hooks/useUser'
import AddFriend from '../../Icons/AddFriend'
import Close from '../../Icons/Close'
import Friended from '../../Icons/Friended'
import Ignore from '../../Icons/Ignore'
import MessageIcon from '../../Icons/Message'
import NoteOutline from '../../Icons/NoteOutline'
import NoteSolid from '../../Icons/NoteSolid'
import Report from '../../Icons/Report'
import BadgeAvatar from '../../common/BadgeAvatar'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import layers from '../../styles/layers'
import shape from '../../styles/shape'
import { PAPER_HEIGHTS } from '../../theme'
import EmbeddedNotes from '../MyNotes/EmbeddedNotes'
import Backdrop from './common/Backdrop'

const lightQuickMenuHeight = 40
const darkQuickMenuHeight = 42
const quickMenuWidth = 290
// const Arrow = styled('div')({
//     position: 'absolute',
//     width: 0,
//     height: 0,
//     zIndex: PAPER_HEIGHTS.tip,
// })

// const arrowDown = css({
//     borderLeft: '8px solid transparent',
//     borderRight: '8px solid transparent',
//     borderTop: `8px solid ${palette.backgroundGrid}`,
// })

const QuickMenuRoot = styled('div')({
    position: 'absolute',
    zIndex: PAPER_HEIGHTS.quickReply,
    // padding: getThemeSpacing(0.25),
    background: palette.backgroundPaper,
    display: 'flex',
    flexFlow: 'column nowrap',
    borderRadius: 20,
    transition: 'height 0.1s ease-in, opacity 0.33s ease-in-out',
    // height: quickMenuHeight,
    opacity: 0,
    border: palette.layerLightBorder,
    boxShadow: layers.one,
})

const notesContainerStyles = css({
    // marginTop: getThemeSpacing(0.25),
})

const quickMenuActionStyles = css({
    padding: getThemeSpacing(1),
    border: 0,
})

const QuickMenuName = styled('div')({
    position: 'absolute',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'center',
    opacity: 0,
    zIndex: PAPER_HEIGHTS.quickReply,
    transition: 'opacity 0.33s ease-in-out',
    width: quickMenuWidth,
    '& > span': {
        borderTopLeftRadius: shape.borderRadiusMediumLarge,
        borderTopRightRadius: shape.borderRadiusMediumLarge,
        padding: `${getThemeSpacing(0.25)} ${getThemeSpacing(2)}`,
    },
})

const QuickMenuContainer = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '& > div': {
        // notes container targeting
        width: '100%',
        borderRadius: '0 0 20px 20px',
    },
    '& textarea': {
        // notes textarea targeting
        width: '100%',
        borderRadius: '0 0 16px 16px',
    },
    transition: 'transform 0.3s var(--spring-ease)',
    transform: 'scale(1)',
    '@starting-style': {
        transform: 'scale(0.95)',
    },
})

const darkModeBackground = css({
    background: palette.backgroundPaper,
})
const lightModeBackground = css({
    background: palette.backgroundGrid,
})

const QuickMenu = memo(() => {
    const { position, id, elevated, show } = useQuickMenu()
    const isAuthenticated = useAppStateSelector(
        AppStateType.APP,
        'authenticated',
    )
    const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
    const fullWindow = useLayoutSelector('fullWindow')
    const isFriend = useUserSelector(id, 'isFriend')
    // const isBookmark = useUserSelector(id, 'isBookmark')
    const hasNote = useUserSelector(id, 'hasNote')
    // const isModel = useUserSelector(id, 'isModel')
    const name = useUserSelector(id, 'name')
    const color = useUserSelector(id, 'color')
    const isIgnored = useUserSelector(id, 'isIgnored')
    const backdropRef = useRef(null)
    const [showNotes, setShowNotes] = useState(false)
    const userColor = getUserColor(color)
    const usableColor = getModelColor(userColor, darkMode || fullWindow)
    const quickMenuHeight = darkMode
        ? darkQuickMenuHeight
        : lightQuickMenuHeight
    const windowWidth = useScreenStateSelector('windowWidth')
    const windowHeight = useScreenStateSelector('windowHeight')

    const { x, y } = position || { y: 0, x: 0 }
    // by default position it above and centered
    const positionStyle = {
        top: y - (quickMenuHeight + 8),
        left: x - quickMenuWidth / 2,
        opacity: 1,
        height: showNotes ? 208 + quickMenuHeight : quickMenuHeight,
        // border: darkMode ? '1px solid ' + palette.backgroundGrid : undefined,
        // boxShadow: darkMode ? undefined : layers.one,
        zIndex: elevated ? PAPER_HEIGHTS.drawer : PAPER_HEIGHTS.quickReply,
    }

    // adjust away from left side if too close
    if (positionStyle.left <= 18) {
        positionStyle.left = 18
    }
    // adjust away from right side if too close
    if (positionStyle.left + quickMenuWidth >= windowWidth - 18) {
        positionStyle.left = windowWidth - quickMenuWidth - 18
    }

    // center it on smaller devices
    if (windowWidth <= APP_BREAKPOINTS.s) {
        positionStyle.left = windowWidth / 2 - quickMenuWidth / 2
    }

    // adjust position up if show notes will offscreen it
    if (showNotes && y + 220 > windowHeight) {
        positionStyle.top -= 200
    }

    const namePositionStyle = {
        top: positionStyle.top - 26,
        left: positionStyle.left,
        opacity: 1,
        color: usableColor,
        zIndex: elevated ? PAPER_HEIGHTS.drawer : PAPER_HEIGHTS.quickReply,
    }

    const onPm = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        closeQuickMenu()
        return openQuickReply(id, { size: FloaterSize.Small })
    }, [isAuthenticated, id])
    const onFriend = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        if (isFriend) removeFriend(id)
        else addFriend(id)
        closeQuickMenu()
    }, [isAuthenticated, isFriend, id])
    // const onBookmarkModel = useCallback(() => {
    //     if (!isAuthenticated) {
    //         openAuthenticationDrawer()
    //         return
    //     }
    //     if (isBookmark) removeBookmark(id)
    //     else bookmarkModel(id)
    //     closeQuickMenu()
    // }, [isAuthenticated, isBookmark, id])
    const onIgnore = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        ignoreUser(id)
        closeQuickMenu()
    }, [isAuthenticated, id])
    const onReport = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        openReportUserDrawer({ id, name })
        closeQuickMenu()
    }, [isAuthenticated, id, name])
    const onNotes = useCallback(() => {
        if (!isAuthenticated) {
            // closeDialog(true)
            openAuthenticationDrawer()
            return
        }
        // expand this quick menu down to show the
        setShowNotes((showNotes) => !showNotes)
    }, [isAuthenticated])
    const containerClick = useCallback((event) => {
        if (event.target === backdropRef.current) {
            closeQuickMenu()
        }
    }, [])
    const onClose = useCallback(() => {
        closeQuickMenu()
    }, [])
    useEffect(() => {
        if (!show) setShowNotes(() => false)
    }, [show])

    // {isModel && (
    //     <QuickMenuAction onClick={onBookmarkModel}>
    //         (!isBookmark ? <AddBookmark title="Add Bookmark" /> :{' '}
    //         <Bookmarked title="Remove Bookmark" />)
    //     </QuickMenuAction>
    // )}
    if (!show) return <QuickMenuRoot />

    return (
        <>
            {isTouchDevice && (
                <QuickMenuName
                    data-testid="QuickMenu__Name"
                    style={namePositionStyle}
                >
                    <span
                        css={
                            darkMode ? darkModeBackground : lightModeBackground
                        }
                    >
                        {name}
                    </span>
                </QuickMenuName>
            )}
            <QuickMenuRoot data-testid="QuickMenu__Root" style={positionStyle}>
                <Backdrop
                    data-testid="Dialog__Backdrop"
                    ref={backdropRef}
                    aria-hidden
                    onClick={containerClick}
                />
                <QuickMenuContainer>
                    <BadgeAvatar
                        title={name}
                        size={40}
                        id={id}
                        disableDrag
                        disableBadge
                    />
                    <IconButton onClick={onFriend} css={quickMenuActionStyles}>
                        {!isFriend ? (
                            <AddFriend />
                        ) : (
                            <Friended title="Remove Friend" />
                        )}
                    </IconButton>
                    <IconButton onClick={onPm} css={quickMenuActionStyles}>
                        <MessageIcon title="Send Message" />
                    </IconButton>
                    <IconButton onClick={onNotes} css={quickMenuActionStyles}>
                        {!hasNote ? (
                            <NoteOutline
                                title={`${showNotes ? 'Close' : 'Open'} Notes`}
                            />
                        ) : (
                            <NoteSolid
                                title={`${showNotes ? 'Close' : 'Open'} Notes`}
                            />
                        )}
                    </IconButton>
                    <IconButton onClick={onIgnore} css={quickMenuActionStyles}>
                        <Ignore
                            filled={isIgnored}
                            title={`${isIgnored ? 'Un-i' : 'I'}gnore User`}
                        />
                    </IconButton>
                    <IconButton onClick={onReport} css={quickMenuActionStyles}>
                        <Report title="Report User" />
                    </IconButton>
                    <IconButton onClick={onClose} css={quickMenuActionStyles}>
                        <Close />
                    </IconButton>
                </QuickMenuContainer>
                <QuickMenuContainer
                    css={showNotes ? notesContainerStyles : null}
                >
                    {showNotes && <EmbeddedNotes userId={id} />}
                </QuickMenuContainer>
            </QuickMenuRoot>
        </>
    )
    // {!showNotes && <Arrow css={arrowDown} style={arrowPosition} />}
})

export default QuickMenu
